<script setup>
// core
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import {ref, computed, onMounted, onUnmounted} from 'vue'
import {useRoute} from "vue-router";
import {useWindowSize} from "@vueuse/core";

// components
import ConnectNumberButton from "@/components/Header/ConnectNumberButton.vue";
import UserBar from "@/components/Header/UserBar.vue";

// data
import MESSENGERS, {TIKTOK} from "@/data/messengers.js";
import viberTelephonySvg from "@/assets/icons/messengers/viber_telephony.svg";

const {t: $t} = useI18n()
const store = useStore()
const route = useRoute()
const fixed = ref(false)
const {width} = useWindowSize()

// computed
const hasEarlyAccess = computed(() => store.getters['auth/hasEarlyAccess'])
const isDividerVisible = computed(() => width.value > 1200)
const messengersList = computed(() => Object.values(MESSENGERS))
const isTariffsPage = computed(() => {
    return route.path === "/profile/tariffs" &&
        window.innerWidth > 1024 && fixed.value &&
        (store.state.tariffsTab === "payment" ||
            store.state.tariffsTab === "calculator")
})
const telephonyItem = computed(() => {
    return {
        color: '#7360F2',
        value: $t('general.user_bar.viber_telephony'),
        svg: viberTelephonySvg,
        ribbon: "Free Beta"
    }
})

// methods
const handleScroll = () => {
    const scrollTop = document.documentElement.scrollTop;
    const isScrolled = scrollTop > 0;

    if (isScrolled !== fixed.value) {
        fixed.value = isScrolled;
    }
}

const handleAction = () => {
    // openDialog also flips isDialogOpen; setDialog alone only swaps the component
    store.commit('openDialog', 'TelephonyCrmDialog')
}

const handleTiktokAction = () => {
    store.commit('openDialog', 'TiktokCrmDialog')
}

//  hooks
onMounted(() => {
    window.addEventListener("scroll", handleScroll);
})

onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
})
</script>

<template>
    <div class="header" :class="{ 'header-tariffs': isTariffsPage }">
        <div class="connect_numbers_block">
            <ConnectNumberButton
                v-for="msgr in messengersList"
                :key="msgr?.value"
                :messenger="msgr"
                :hasRibbon="!!msgr.ribbon"
            />
            <template v-if="hasEarlyAccess">
                <ConnectNumberButton
                    :messenger="TIKTOK"
                    :dialog-first="false"
                    :has-ribbon="true"
                    @on-action="handleTiktokAction"
                />
                <div v-if="isDividerVisible" class="header__divider"></div>
                <ConnectNumberButton
                    :messenger="telephonyItem"
                    :dialog-first="false"
                    :has-ribbon="true"
                    @on-action="handleAction"
                />
            </template>
        </div>
        <div class="user-right">
            <UserBar/>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.header-tariffs {
    position: sticky;

    background: white;
    z-index: 10;
    top: 0;
    -webkit-box-shadow: 0px 3px 33px -6px rgba(0, 0, 0, 0.24);
    -moz-box-shadow: 0px 3px 33px -6px rgba(0, 0, 0, 0.24);
    box-shadow: 0px 3px 33px -6px rgba(0, 0, 0, 0.24);
}

.btnGroup {
    display: flex;
    width: 55%;
    justify-content: space-between;
}

.userBtn {
    font-family: Gilroy;
    font-weight: 500;
    border: 1px solid transparent;
    border-radius: 11px;
    background-color: #7a70df;
    color: white;
    line-height: 30px;
    font-size: 17px;
    padding: 10px 40px;
}

.positionBtn {
    width: 100%;
    display: flex;
    justify-content: center;
}

.exit {
    width: 17px;
    height: 17px;
    fill: #403e4366;
}

.header {
    padding: 21px 20px 30px;
    display: flex;
    justify-content: space-between;
    gap: 40px;

    &__divider {
        height: 40px;
        width: 1px;
        background: #E9EAEC;
    }
}

.header.header-tariffs {
    padding-bottom: 10px;
    box-shadow: 1px 21px 25px -30px rgba(0, 0, 0, 0.64);
    -webkit-box-shadow: 1px 21px 25px -30px rgba(0, 0, 0, 0.64);
    -moz-box-shadow: 1px 21px 25px -30px rgba(0, 0, 0, 0.64);
}

.connect_numbers_block {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
}

.user-right {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 16px;
}

@media screen and (max-width: 1200px) {
    .connect_numbers_block {
        // flex-direction: column;
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 16px;
        max-width: 500px;
    }

    .user-right {
        flex-wrap: wrap;
        // width: 288px;
        flex: 1;
        align-content: flex-start;
        justify-content: flex-end;
    }
}

// the profile takes the first row, the buttons fill the next ones as a grid;
// at 580 and below they turn back into today's full-width list
@media screen and (min-width: 581px) and (max-width: 1200px) {
    .header {
        flex-wrap: wrap;
        gap: 16px 24px;
    }

    .connect_numbers_block {
        order: 2;
        flex: 1 1 100%;
        max-width: none;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
        gap: 10px;
    }

    .user-right {
        flex: 1 1 100%;
        justify-content: flex-end;
    }
}

@media screen and (max-width: 580px) {
    .header {
        flex-direction: column;
        gap: 8px;

        .connect_numbers_block {
            order: 2;
        }
    }
    .user-right {
        justify-content: space-between;
        width: auto;
    }
}
</style>
